import React, { useState } from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {Link, useLocation, useNavigate} from 'react-router-dom';
import AccountHeroBit from '../../components/account/AccountHeroBit';
import LuxuryInput from '../../components/ui/LuxuryInput';
import {FaCheck, FaArrowRight, FaInfoCircle, FaExclamationTriangle} from 'react-icons/fa';
import { fadeInUp, pageTransition } from '../../components/animations/animations';
import {AuthService} from "../../api/auth.api";

const Register = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        agreeTerms: false,
        subscribe: true
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [passwordStrength, setPasswordStrength] = useState(0);
    const [showApiError, setShowApiError] = useState(false);
    const [apiError, setApiError] = useState('');
    const [fieldErrors, setFieldErrors] = useState([]);

    const checkPasswordStrength = (password) => {
        let strength = 0;
        if (password.length >= 8) strength++;
        if (/[A-Z]/.test(password)) strength++;
        if (/[a-z]/.test(password)) strength++;
        if (/[0-9]/.test(password)) strength++;
        if (/[^A-Za-z0-9]/.test(password)) strength++;
        setPasswordStrength(strength);
    };

    const validateForm = () => {
        const newErrors = {};
        const newFieldErrors = [];

        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
            newFieldErrors.push({ field: 'name', message: 'Please enter your full name' });
        } else if (formData.name.trim().split(' ').length < 2) {
            newErrors.name = 'Please enter first and last name';
            newFieldErrors.push({ field: 'name', message: 'Please include both first and last name' });
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
            newFieldErrors.push({ field: 'email', message: 'Please enter your email address' });
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
            newFieldErrors.push({ field: 'email', message: 'Email format is invalid (example@domain.com)' });
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
            newFieldErrors.push({ field: 'password', message: 'Please create a secure password' });
        } else if (formData.password.length < 8) {
            newErrors.password = 'Password must be at least 8 characters';
            newFieldErrors.push({ field: 'password', message: 'Minimum 8 characters required' });
        } else if (passwordStrength < 3) {
            newErrors.password = 'Password is too weak';
            newFieldErrors.push({ field: 'password', message: 'Include uppercase, lowercase, numbers & special characters' });
        }

        if (formData.password !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Passwords do not match';
            newFieldErrors.push({ field: 'confirmPassword', message: 'Passwords must match exactly' });
        }

        if (!formData.agreeTerms) {
            newErrors.agreeTerms = 'You must agree to the terms';
            newFieldErrors.push({ field: 'agreeTerms', message: 'Please agree to Terms of Service and Privacy Policy' });
        }

        setErrors(newErrors);
        setFieldErrors(newFieldErrors);

        // Add shake animation for validation errors
        if (Object.keys(newErrors).length > 0) {
            document.getElementById('register-form').style.animation = 'shake 0.5s';
            setTimeout(() => {
                document.getElementById('register-form').style.animation = '';
            }, 500);
        }

        return Object.keys(newErrors).length === 0;
    };

    const parseBackendError = (error) => {
        // Check if it's a structured error from backend
        if (error.errors && Array.isArray(error.errors)) {
            const fieldErrors = {};
            const errorMessages = [];

            error.errors.forEach(err => {
                if (err.field) {
                    fieldErrors[err.field] = err.message;
                }
                errorMessages.push(err.message);
            });

            setErrors(fieldErrors);
            setApiError(errorMessages.join('. '));
            setFieldErrors(error.errors);
        } else if (error.message) {
            // Generic error message
            let message = error.message;

            // Map common backend errors to user-friendly messages
            const errorMap = {
                'Email already registered': 'This email is already associated with an account',
                'Invalid email or password': 'Please check your email and password',
                'Password must be at least 8 characters': 'Password must be at least 8 characters long',
                'Please enter a valid email address': 'Please enter a valid email address',
                'Passwords do not match': 'Passwords do not match',
                'Current password is incorrect': 'Current password is incorrect',
                'Invalid or expired reset token': 'Password reset link has expired',
                'User not found': 'Account not found with this email',
                'Account is deactivated': 'This account has been deactivated',
                'Invalid refresh token': 'Session expired, please login again',
                'secretOrPrivateKey must have a value': 'System error - please try again later',
                'next is not a function': 'System error - please try again',
                'Network Error': 'Connection failed. Please check your internet',
                'Failed to fetch': 'Unable to connect to server'
            };

            message = errorMap[message] || message;
            setApiError(message);
            setShowApiError(true);

            // Auto-hide after 5 seconds
            setTimeout(() => {
                setShowApiError(false);
            }, 5000);
        }

        // Add subtle shake animation
        setTimeout(() => {
            document.getElementById('register-form').style.animation = 'shake 0.3s';
            setTimeout(() => {
                document.getElementById('register-form').style.animation = '';
            }, 300);
        }, 100);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        setIsLoading(true);
        setErrors({});
        setApiError('');
        setShowApiError(false);
        setFieldErrors([]);

        try {
            const [firstName, ...lastNameParts] = formData.name.trim().split(' ');
            const lastName = lastNameParts.join(' ') || ' ';

            const payload = {
                firstName,
                lastName,
                email: formData.email,
                password: formData.password,
                confirmPassword: formData.confirmPassword,
                newsletterSubscribed: formData.subscribe
            };

            console.log('Register payload:', payload);
            const res = await AuthService.register(payload);



            // Store user data
            localStorage.setItem("kromaic_user", JSON.stringify(res.user));

            // Check if we have saved checkout data
            const savedCheckoutData = localStorage.getItem('kromaic_checkout_recovery');
            const isFromCheckout = location.state?.preserveCheckout ||
                '/checkout' === location.state?.from ||
                location.state?.source === 'checkout';


            // Mark that we're coming from register (for checkout flow)
            localStorage.setItem('kromaic_from_register', 'true');

            if (isFromCheckout && savedCheckoutData) {
                console.log('Register: User registered from checkout, redirecting back');



                // Redirect to checkout with saved data
                setTimeout(() => {
                    navigate('/checkout', {
                        state: {
                            restoreCheckout: true,
                            fromRegister: true,
                            autoLogin: true
                        },
                        replace: true
                    });
                }, 1000);
            } else {

                setTimeout(() => {
                    navigate('/account', { replace: true });
                }, 1000);
            }

        } catch (err) {
            console.error('Registration error:', err);
            parseBackendError(err);
        } finally {
            setIsLoading(false);
        }
    };


    const getStrengthColor = () => {
        switch(passwordStrength) {
            case 5: return 'bg-sage';
            case 4: return 'bg-champagne';
            case 3: return 'bg-coral';
            default: return 'bg-error';
        }
    };

    const getStrengthText = () => {
        switch(passwordStrength) {
            case 5: return 'Excellent';
            case 4: return 'Strong';
            case 3: return 'Good';
            case 2: return 'Weak';
            default: return 'Very Weak';
        }
    };


    return (
        <motion.div
            variants={pageTransition}
            initial="initial"
            animate="animate"
            exit="exit"
            className="min-h-screen bg-gradient-to-b from-pearl to-nude/30 py-12"
        >
            <div className="max-w-4xl mx-auto px-4">
                <AccountHeroBit
                    title="Begin your journey"
                    subtitle="Join the KROMAIC ritual and discover personalized beauty"
                    background="sage"
                />

                <motion.div
                    id="register-form"
                    variants={fadeInUp}
                    className="bg-white rounded-3xl shadow-luxury p-8 md:p-12 relative overflow-hidden"
                >
                    {/* Error highlight effect */}
                    {fieldErrors.length > 0 && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="absolute inset-0 border-2 border-error/10 rounded-3xl pointer-events-none"
                        />
                    )}

                    <h2 className="font-heading text-2xl font-medium text-charcoal mb-8">
                        Create your account
                    </h2>

                    <form onSubmit={handleSubmit} className="space-y-8">
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="relative">
                                <LuxuryInput
                                    label="Full Name"
                                    placeholder="First and Last name"
                                    value={formData.name}
                                    onChange={(e) =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                    error={errors.name}
                                    required
                                    size="large"
                                    icon={errors.name ? FaExclamationTriangle : null}
                                    iconColor={errors.name ? "error" : null}
                                />
                                {errors.name && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-xs text-error mt-2 flex items-center gap-1"
                                    >
                                        <FaInfoCircle className="w-3 h-3" />
                                        {errors.name}
                                    </motion.p>
                                )}
                            </div>

                            <div className="relative">
                                <LuxuryInput
                                    type="email"
                                    label="Email Address"
                                    placeholder="your.email@example.com"
                                    value={formData.email}
                                    onChange={(e) =>
                                        setFormData({ ...formData, email: e.target.value })
                                    }
                                    error={errors.email}
                                    required
                                    size="large"
                                    icon={errors.email ? FaExclamationTriangle : null}
                                    iconColor={errors.email ? "error" : null}
                                />
                                {errors.email && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-xs text-error mt-2 flex items-center gap-1"
                                    >
                                        <FaInfoCircle className="w-3 h-3" />
                                        {errors.email}
                                    </motion.p>
                                )}
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            <div>
                                <div className="relative">
                                    <LuxuryInput
                                        type="password"
                                        label="Password"
                                        placeholder="Create a secure password"
                                        value={formData.password}
                                        onChange={(e) => {
                                            setFormData({
                                                ...formData,
                                                password: e.target.value
                                            });
                                            checkPasswordStrength(e.target.value);
                                        }}
                                        error={errors.password}
                                        required
                                        size="large"
                                        icon={errors.password ? FaExclamationTriangle : null}
                                        iconColor={errors.password ? "error" : null}
                                    />
                                </div>

                                {formData.password && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        className="mt-4 space-y-3"
                                    >
                                        <div>
                                            <div className="flex items-center justify-between mb-2">
                                                <span className="text-xs text-charcoal/50">
                                                    Password Strength
                                                </span>
                                                <span className={`text-xs font-medium ${getStrengthColor().replace('bg-', 'text-')}`}>
                                                    {getStrengthText()}
                                                </span>
                                            </div>
                                            <div className="flex gap-1 mb-1">
                                                {[1, 2, 3, 4, 5].map((level) => (
                                                    <motion.div
                                                        key={level}
                                                        initial={{ scaleX: 0 }}
                                                        animate={{ scaleX: level <= passwordStrength ? 1 : 0 }}
                                                        transition={{ delay: level * 0.1 }}
                                                        className={`h-2 flex-1 rounded-full ${
                                                            level <= passwordStrength
                                                                ? getStrengthColor()
                                                                : 'bg-nude/20'
                                                        }`}
                                                    />
                                                ))}
                                            </div>
                                        </div>

                                        {/* Password Requirements */}
                                        <div className="space-y-2">
                                            {[
                                                { text: 'At least 8 characters', valid: formData.password.length >= 8 },
                                                { text: 'One uppercase letter', valid: /[A-Z]/.test(formData.password) },
                                                { text: 'One lowercase letter', valid: /[a-z]/.test(formData.password) },
                                                { text: 'One number', valid: /[0-9]/.test(formData.password) },
                                                { text: 'One special character', valid: /[^A-Za-z0-9]/.test(formData.password) }
                                            ].map((req, idx) => (
                                                <motion.div
                                                    key={idx}
                                                    initial={{ opacity: 0, x: -10 }}
                                                    animate={{ opacity: 1, x: 0 }}
                                                    transition={{ delay: idx * 0.1 }}
                                                    className="flex items-center gap-2"
                                                >
                                                    <div className={`w-4 h-4 rounded-full flex items-center justify-center ${req.valid ? 'bg-sage/20 text-sage' : 'bg-nude/20 text-charcoal/30'}`}>
                                                        {req.valid ? (
                                                            <FaCheck className="w-2 h-2" />
                                                        ) : (
                                                            <div className="w-1 h-1 rounded-full bg-current" />
                                                        )}
                                                    </div>
                                                    <span className={`text-xs ${req.valid ? 'text-charcoal/60' : 'text-charcoal/40'}`}>
                                                        {req.text}
                                                    </span>
                                                </motion.div>
                                            ))}
                                        </div>
                                    </motion.div>
                                )}
                            </div>

                            <div className="relative">
                                <LuxuryInput
                                    type="password"
                                    label="Confirm Password"
                                    placeholder="Re-enter your password"
                                    value={formData.confirmPassword}
                                    onChange={(e) =>
                                        setFormData({
                                            ...formData,
                                            confirmPassword: e.target.value
                                        })
                                    }
                                    error={errors.confirmPassword}
                                    required
                                    size="large"
                                    icon={errors.confirmPassword ? FaExclamationTriangle : null}
                                    iconColor={errors.confirmPassword ? "error" : null}
                                />
                                {errors.confirmPassword && (
                                    <motion.p
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="text-xs text-error mt-2 flex items-center gap-1"
                                    >
                                        <FaInfoCircle className="w-3 h-3" />
                                        {errors.confirmPassword}
                                    </motion.p>
                                )}
                            </div>
                        </div>

                        <div className="space-y-6">
                            <motion.div
                                whileHover={{ scale: 1.01 }}
                                className={`p-4 rounded-2xl transition-all ${errors.agreeTerms ? 'bg-error/5 border border-error/20' : 'bg-nude/5'}`}
                            >
                                <label className="flex items-start gap-4 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.agreeTerms}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                agreeTerms: e.target.checked
                                            })
                                        }
                                        className="mt-1 w-5 h-5 rounded-lg border-nude text-champagne focus:ring-champagne/30 focus:ring-2 focus:ring-offset-2 transition-all"
                                    />
                                    <div className="flex-1">
                                        <p className="text-sm text-charcoal font-medium">
                                            I agree to KROMAIC's terms
                                        </p>
                                        <p className="text-xs text-charcoal/60 mt-1">
                                            By creating an account, you agree to our{' '}
                                            <Link to="/terms" className="text-champagne hover:underline">
                                                Terms of Service
                                            </Link>{' '}
                                            and{' '}
                                            <Link to="/privacy" className="text-champagne hover:underline">
                                                Privacy Policy
                                            </Link>
                                        </p>
                                    </div>
                                </label>
                            </motion.div>

                            {errors.agreeTerms && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    className="bg-error/5 border border-error/20 rounded-xl p-3"
                                >
                                    <p className="text-xs text-error flex items-center gap-2">
                                        <FaExclamationTriangle className="w-3 h-3 flex-shrink-0" />
                                        {errors.agreeTerms}
                                    </p>
                                </motion.div>
                            )}

                            <motion.div
                                whileHover={{ scale: 1.01 }}
                                className="p-4 rounded-2xl bg-nude/5 transition-all"
                            >
                                <label className="flex items-start gap-4 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.subscribe}
                                        onChange={(e) =>
                                            setFormData({
                                                ...formData,
                                                subscribe: e.target.checked
                                            })
                                        }
                                        className="mt-1 w-5 h-5 rounded-lg border-nude text-champagne focus:ring-champagne/30 focus:ring-2 focus:ring-offset-2 transition-all"
                                    />
                                    <div>
                                        <p className="text-sm text-charcoal font-medium">
                                            Stay connected with KROMAIC
                                        </p>
                                        <p className="text-xs text-charcoal/60 mt-1">
                                            Subscribe to receive exclusive offers, early access to collections, and personalized beauty insights
                                        </p>
                                    </div>
                                </label>
                            </motion.div>
                        </div>

                        {/* Field-specific error summary */}
                        <AnimatePresence>
                            {fieldErrors.length > 0 && (
                                <motion.div
                                    initial={{ opacity: 0, height: 0 }}
                                    animate={{ opacity: 1, height: 'auto' }}
                                    exit={{ opacity: 0, height: 0 }}
                                    className="bg-nude/10 border border-nude/20 rounded-2xl p-4"
                                >
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-8 h-8 rounded-full bg-error/10 flex items-center justify-center">
                                            <FaExclamationTriangle className="w-4 h-4 text-error" />
                                        </div>
                                        <h4 className="text-sm font-medium text-charcoal">
                                            Please check the following:
                                        </h4>
                                    </div>
                                    <ul className="space-y-2">
                                        {fieldErrors.map((error, idx) => (
                                            <motion.li
                                                key={idx}
                                                initial={{ opacity: 0, x: -10 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: idx * 0.1 }}
                                                className="flex items-center gap-2 text-sm text-charcoal/70"
                                            >
                                                <div className="w-1.5 h-1.5 rounded-full bg-error" />
                                                {error.message}
                                            </motion.li>
                                        ))}
                                    </ul>
                                </motion.div>
                            )}
                        </AnimatePresence>

                        <motion.button
                            type="submit"
                            disabled={isLoading}
                            whileHover={{ scale: isLoading ? 1 : 1.02 }}
                            whileTap={{ scale: isLoading ? 1 : 0.98 }}
                            className={`
                                w-full px-8 py-5 rounded-full
                                bg-gradient-to-r from-champagne via-champagne/90 to-champagne/80
                                text-charcoal font-medium text-lg
                                hover:from-champagne/90 hover:via-champagne/80 hover:to-champagne/70
                                transition-all duration-300
                                shadow-lg hover:shadow-xl
                                disabled:opacity-50 disabled:cursor-not-allowed
                                relative overflow-hidden group
                            `}
                        >
                            <span className="relative flex items-center justify-center gap-3">
                                {isLoading ? (
                                    <>
                                        <span className="w-5 h-5 border-2 border-charcoal/60 border-t-transparent rounded-full animate-spin" />
                                        <span className="relative">
                                            Creating account
                                            <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-charcoal/20">
                                                <motion.span
                                                    className="absolute h-full bg-champagne"
                                                    animate={{
                                                        x: ['0%', '100%'],
                                                    }}
                                                    transition={{
                                                        duration: 1.5,
                                                        repeat: Infinity,
                                                        ease: "linear"
                                                    }}
                                                    style={{ width: '30%' }}
                                                />
                                            </span>
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <span>Begin Your Journey</span>
                                        <FaArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                                    </>
                                )}
                            </span>

                            {/* Shimmer effect */}
                            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                        </motion.button>

                        <p className="text-center text-charcoal/50 text-sm">
                            Already part of KROMAIC?{' '}
                            <Link
                                to="/login"
                                state={{
                                    from: location.state?.from || '/checkout',
                                    source: 'checkout',
                                    preserveCheckout: true
                                }}
                                className="text-champagne hover:text-champagne/80 font-medium transition-colors"
                            >
                                Sign in to your account
                            </Link>
                        </p>
                    </form>
                </motion.div>

                <motion.div
                    variants={fadeInUp}
                    transition={{ delay: 0.3 }}
                    className="mt-12 grid md:grid-cols-3 gap-6"
                >
                    {[
                        {
                            title: 'Exclusive Access',
                            description: 'Early access to new collections and limited editions'
                        },
                        {
                            title: 'Personalized Experience',
                            description: 'Tailored recommendations based on your profile'
                        },
                        {
                            title: 'Members-Only Rewards',
                            description: 'Earn points with every purchase'
                        }
                    ].map((benefit, index) => (
                        <motion.div
                            key={index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 * index }}
                            whileHover={{ y: -4 }}
                            className="p-6 bg-white rounded-2xl shadow-subtle text-center"
                        >
                            <div className="w-12 h-12 rounded-full bg-sage/10 flex items-center justify-center mx-auto mb-4">
                                <FaCheck className="w-5 h-5 text-sage" />
                            </div>
                            <h3 className="font-medium text-charcoal mb-2">
                                {benefit.title}
                            </h3>
                            <p className="text-sm text-charcoal/60">
                                {benefit.description}
                            </p>
                        </motion.div>
                    ))}
                </motion.div>
            </div>
        </motion.div>
    );
};

export default Register;
